import { $, _, numeral } from "js/vendor";
import { app } from "js/namespaces";
import { Component, Fragment } from "react";
import React from "reactn";
import { parseCSV } from "js/core/utilities/utilities";
import CheckBox from "js/react/components/CheckBox";
import ErrorPane from "js/react/components/ErrorPane";
import { DialogTitle, DialogActions, Button, Tooltip, Icon } from "@material-ui/core";
import { Info } from "@material-ui/icons";
import { ToggleButtonGroup, ToggleButton } from "@material-ui/lab";
import { BeautifulDialog, DialogContent, ShowWarningDialog } from "./BaseDialog";
import styled from "styled-components";
import { getStaticUrl } from "js/config";
import { clipboardRead, ClipboardType } from "js/core/utilities/clipboard";

const DialogTitleSplitter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 34px 34px 30px;

  .MuiDialogTitle-root {
      padding: 0;

      h2 {
          display: flex;
          align-items: center;
      }
  }

  .MuiToggleButton-root {
      font-weight: 600;

      &.Mui-selected {
        color: white;
        background: #11A9E2;
        border-color: #11A9E2;
        cursor: default;

        &:hover {
            background: #11A9E2;
        }
    }
  }
`;

const CompositeLabel = styled.span`
    display: flex;
    align-items: center;
    padding: 10px;

    * {
        margin-left: 5px;

        &:first-child {
            margin-left: 0;
        }
    }
`;

const ChartDialogContent = styled(DialogContent)`
    .highlight-series {
        background: #DBE9F0;
    }
    .highlight-category {
        background: #FFF1D9;
    }
    .ignored {
        background: #ccc;
        color: #777;
        border-color: #bbb !important;
    }

    .MuiButton-contained {
        color: white;
        background: #11A9E2;
        padding: 8px;
        min-width: auto;
        height: auto;

        &:hover {
            box-shadow: none;
            background: #66A9E2;
        }
    }

    .content-toolip-parent {
        display: flex;
        align-items: center;
        justify-content: center;
        
        label {
            display: flex;
            align-items: center;
    
            .MuiTypography-root {
                text-transform: uppercase;
                font-size: 0.8rem;
                font-weight: 600;
            }
        }
    }

    .table-wrapper {
        width: 100%;
        max-width: 100%;
        height: auto;
        max-height: 400px;
        margin-bottom: 20px;
        overflow: auto;
        padding-right: 1px; /* fix rendering bug: if we don't do this, the scroll bar shows when it shouldn't */

        .table {
            width: 100%;
            max-width: 100%;
            border-spacing: 0px;
            border-collapse: collapse;

            th,
            td {
                border: solid 1px #ccc;
                padding: 5px;
                line-height: 1.42857143;
                text-align: center;
                white-space: nowrap;

                &.table-label {
                    border: none;
                    font-weight: 500;
                    padding: 5px 10px;
                }
            }
        }
    }
`;

const ChartDialogActions = styled(DialogActions)`
    &.MuiDialogActions-root {
        justify-content: space-between;

        .MuiButton-root {
            padding: 6px 16px;
            font-size: 1.0rem;
        }
    }
`;

export default class ImportChartDataDialog extends Component {
    state = {
        data: [],
        showPasteMessage: true,
        hasCategoryNames: true,
        hasSeriesNames: false,
        plotColumnsAsSeries: false,
    };

    lockSlide = true;
    numberRegex = /^(-?)[$\xA2-\xA5\u058F\u060B\u09F2\u09F3\u09FB\u0AF1\u0BF9\u0E3F\u17DB\u20A0-\u20BD\uA838\uFDFC\uFE69\uFF04\uFFE0\uFFE1\uFFE5\uFFE6]?(\s\()?(([1-9][0-9]{0,2}([,.][0-9]{3})*)|[0-9]+)?([,.][0-9]{1,})?(\)?)%?$/;
    negativeNumberParanthesisRegex = /(\$)\(((\d|,|\.)+)\)/g;
    spacesRegex = / {4,}/g;

    constructor() {
        super();

        this.tooltipRef = React.createRef();
    }

    componentDidMount() {
        const {
            pieSeries,
        } = this.props;

        this.setState({
            error: null,
            data: [],
            cleanInput: null,
            showPasteMessage: true,
            hasCategoryNames: true,
            hasSeriesNames: !pieSeries,
            plotColumnsAsSeries: false,
            appendData: false,
        });

        $(window).on("paste.dialog", async event => {
            const pasteWarning = message => ShowWarningDialog({
                title: "Invalid data in clipboard",
                message,
            });

            let text = await clipboardRead([ClipboardType.TEXT], event);
            if (text) {
                const cleanInput = this.clearClipboardTextFormat(text);
                this.setState({
                    cleanInput,
                });
                this.processInput();

                const {
                    error,
                    data,
                } = this.state;

                if (error) {
                    pasteWarning(error);
                    return;
                }

                if (!this.state.plotColumnsAsSeries && (data.length > 11)) {
                    ShowWarningDialog({
                        title: "Cannot paste data!",
                        message: "This chart does not support that much data.",
                    });
                    return;
                }
                this.setState({
                    data,
                    showPasteMessage: false,
                });
            } else {
                pasteWarning("Please paste tabular data with numeric series from Excel, Google Sheets or another spreadsheet application");
            }
        });
    }

    componentWillUnmount() {
        $(window).off("paste.dialog");
    }

    clearClipboardTextFormat(text) {
        let result = text.replace(this.negativeNumberParanthesisRegex, "-$1$2");

        // Only treat spaces as tabs if there are no tabs present
        if (!result.includes("\t")) {
            result = result.replace(this.spacesRegex, "\t");
        }
        return result;
    }

    processInput(changedState = null) {
        const {
            pieSeries,
            isWaterfall,
        } = this.props;
        const {
            cleanInput,
        } = this.state;

        const result = {
            error: null,
            data: this.state.data,
            plotColumnsAsSeries: this.state.plotColumnsAsSeries,
            hasSeriesNames: this.state.hasSeriesNames,
            hasCategoryNames: this.state.hasCategoryNames,
            ...changedState,
        };

        let data = parseCSV(cleanInput, "");
        data = data.map(row => row.map(x => x.trim()));
        data = data.filter((row, i) => {
            // allow an empty cell at (0,0) to be considered truthy for the sake of the .every method
            return row.some((item, j) => (!i && !j) || item);
        });

        // If no data, exit early
        if (!data.length) {
            result.error = "Input data is empty.";
            this.setState(result);
            return false;
        }

        // Calculate max row length
        const colLength = data.map(row => row.length);
        const maxLength = Math.max(...colLength, 0);
        // Pad empty cells
        for (let row of data) {
            const diff = maxLength - row.length;
            for (let i = 0; i < diff; i++) {
                row.push("");
            }
        }
        // single column data doesn't make sense so we push zero as an empty data point
        if (maxLength === 1) {
            data.forEach(row => row.push("0"));
        }

        // If we're processing fresh input, make some assumptions about the data
        if (!changedState) {
            // If more rows than columns, assume columns as series, else rows as series
            result.plotColumnsAsSeries = data.length > data[0].length;
            result.hasCategoryNames = data.length > 1;
        }

        // Transpose the data if we're flagged to do so
        if (result.plotColumnsAsSeries) {
            data = _.unzip(data);
        }

        result.data = data;

        const seriesNames = result.hasCategoryNames ? _.map(data, 0).slice(1) : _.map(data, 0);

        // If we're processing fresh input and this isn't for a pie chart, make further assumptions about the data
        if (!changedState && !pieSeries) {
            result.hasSeriesNames = seriesNames.every(rowItem => !this.numberRegex.test(rowItem));
        }

        const rowStartIndex = result.hasSeriesNames ? 1 : 0;
        const colStartIndex = result.hasCategoryNames ? 1 : 0;

        // Clean values
        for (let y = colStartIndex; y < result.data.length; ++y) {
            const row = result.data[y];
            for (let x = rowStartIndex; x < row.length; ++x) {
                let value = !isWaterfall ? null : "SUM";
                if (numeral.validateEx(row[x])) {
                    value = numeral(row[x]).value();
                }
                row[x] = value;
            }
        }

        this.seriesIndices = [];
        data.forEach((row, rowIndex) => {
            // Only include the first series if we don't have category names
            let includeSeries = rowIndex > 0 || !result.hasCategoryNames;
            // Only include the series if every entry is a number
            if (includeSeries) {
                const series = row.slice(rowStartIndex);
                includeSeries =
                    series.length > 0 &&
                    series.some(x => !!x && x !== "SUM");
            }
            if (includeSeries) {
                this.seriesIndices.push(rowIndex);
            }
        });

        // If an entire series is invalid, then the data is invalid
        if (this.seriesIndices.length === 0) {
            result.error = "No valid series present. Only series and category names are permited to be non-numeric. All other values must be numeric. Series must also have at least one non-zero value.";
            this.setState(result);
            return false;
        }

        // Add missing labels
        {
            // Only bother labeing valid rows
            const validRows = result.data.filter((row, index) => this.seriesIndices.includes(index) || (result.hasCategoryNames && index === 0));
            // Replace any empty cells on the top and left edges with generated labels.
            validRows.forEach((row, indexRow) => {
                if (indexRow === 0) {
                    if (result.hasCategoryNames) {
                        row.forEach((cell, indexCell) => {
                            row[indexCell] = cell || (indexCell === 0 ? "" : `Category ${indexCell + (result.hasSeriesNames ? 0 : 1)}`);
                        });
                    }
                } else if (result.hasSeriesNames) {
                    row[0] = row[0] || `Series ${indexRow + (result.hasCategoryNames ? 0 : 1)}`;
                }
            });
        }

        this.setState(result);

        return true;
    }

    updateModels() {
        const {
            model,
            pieSeries,
            chartElement,
        } = this.props;
        const {
            data,
            hasCategoryNames,
            hasSeriesNames,
            appendData,
        } = this.state;

        // process data for chartData model
        const elementColors = app.currentTheme.palette.getElementColors();
        const stacking = chartElement.model.chartData?.series[0]?.stacking;

        const categoryRow = hasSeriesNames ? data[0].slice(1) : data[0];
        const categoryNames = hasCategoryNames ? categoryRow : categoryRow.map((_, index) => `Category ${index + 1}`);

        const seriesRows = data.filter((row, index) => this.seriesIndices.includes(index));
        const seriesNames = seriesRows.map((row, index) => hasSeriesNames ? row.shift() : `Series ${index + 1}`);

        if (pieSeries) {
            if (!seriesRows[0]) {
                ShowWarningDialog({
                    title: "Invalid data in clipboard",
                    message: "Please paste additional tabular data with numeric series from Excel, Google Sheets or another spreadsheet application",
                });
                return;
            }
            const pieData = seriesRows[0].map((value, index) => ({
                label: { text: categoryNames[index] },
                value: Number(String(value).replace(/[^0-9.-]+/g, ""))
            }));
            pieSeries.data = pieData;
        } else {
            let seriesModels;
            const chartType = model.series[0].type;
            const timestamp = new Date().getTime();
            const isWaterfall = chartType === "waterfall";
            seriesModels = seriesRows.map((row, index) => ({
                id: `series${timestamp + index}`,
                name: seriesNames[index],
                type: chartType,
                data: row.map(val => ({ y: val, pt: true })),
                colorName: elementColors[index % elementColors.length].value,
                marker: chartType.indexOf("area") > -1 ? "circle" : "none",
                stacking,
                zones: isWaterfall ? null : [
                    {
                        style: "default",
                    }
                ],
            }));

            if (categoryNames.length && seriesModels.length) {
                if (!appendData) {
                    model.xAxis = Object.assign({}, model.xAxis, { categories: categoryNames });
                    model.series = seriesModels;
                } else {
                    if (categoryNames.length > model.xAxis.categories.length) {
                        model.xAxis.categories = model.xAxis.categories.concat(Array(categoryNames.length - model.xAxis.categories.length).fill(""));
                    }
                    for (let series of model.series) {
                        if (categoryNames.length > series.data.length) {
                            series.data = series.data.concat(Array(categoryNames.length - series.data.length).fill({ y: null, pt: true }));
                        }
                    }

                    for (let series of seriesModels) {
                        if (series.data.length < model.xAxis.categories.length) {
                            series.data = series.data.concat(Array(model.xAxis.categories.length - series.data.length).fill({ y: null, pt: true }));
                        }
                    }

                    model.series = model.series.concat(seriesModels);
                }
            }
        }
    }

    updateChartData() {
        const {
            tableView,
            chartElement,
        } = this.props;
        const {
            data,
            appendData,
        } = this.state;

        if (data) {
            // Delete all annotations if we're replacing the data.
            if (!appendData) {
                const annotations = chartElement.elements?.annotations;
                if (annotations) {
                    const annotationdIds = annotations.itemCollection.map(item => item.id);
                    annotationdIds.forEach(annotationdId => {
                        annotations.deleteItem(annotationdId);
                    });

                    const connectorIds = annotations.connectors.itemCollection.map(item => item.id);
                    connectorIds.forEach(connectorId => {
                        annotations.connectors.deleteItem(connectorId);
                    });
                }
            }

            this.updateModels();

            chartElement.canvas.updateCanvasModel();
            tableView.refreshRender();

            this.props.closeDialog();
        } else {
            this.props.closeDialog();
        }
    }

    render() {
        const {
            canAppendSeries = true,
            pieSeries,
        } = this.props;

        const {
            error,
            data,
            showPasteMessage,
            hasCategoryNames,
            hasSeriesNames,
            plotColumnsAsSeries,
            appendData,
        } = this.state;

        let dialogContent;
        if (showPasteMessage) {
            const shortcut = window.navigator.platform == "MacIntel" ? "command-V" : "control-v";
            const message = `Paste your spreadsheet data by pressing ${shortcut}`;
            dialogContent = (
                <>
                    <DialogTitle>Import Data</DialogTitle>
                    <DialogContent className="import_chart_data_dialog">
                        <div className="pasteContainer">
                            <div>{message}</div>
                        </div>
                    </DialogContent>
                </>
            );
        } else {
            const colCount = data[0]?.length || 0;
            const categoriesStart = hasSeriesNames ? 1 : 0;
            const seriesStart = hasCategoryNames ? 1 : 0;
            const categoryNames = [];
            !pieSeries && categoryNames.push(null);
            hasSeriesNames && categoryNames.push(null);
            for (let index = categoriesStart; index < colCount; ++index) {
                categoryNames.push(`Category ${index + 1 - categoriesStart}`);
            }

            const categoryLabels = (
                <tr key={`category-row`}>
                    {categoryNames.map((categoryLabel, x) => (
                        <th key={`category-${x}`} className="table-label">{categoryLabel}</th>
                    ))}
                </tr>
            );

            const tableContent = [
                categoryLabels,
                ...data.map((row, y) => {
                    const rowContent = row.map((cellContent, x) => {
                        if (hasCategoryNames && y === 0) {
                            // Display cell [0,0] as ignored if we have both category and series names
                            if (hasSeriesNames && x === 0) {
                                return (<th key={x} className="ignored">{cellContent}</th>);
                                // Highlight the category name
                            } else {
                                return (<th key={x} className="highlight-category">{cellContent}</th>);
                            }
                        } else if (hasSeriesNames && x === 0) {
                            // Display cell [0,0] as ignored if we have both category and series names
                            if (hasCategoryNames && y === 0) {
                                return (<th key={x} className="ignored">{cellContent}</th>);
                            } else if (!this.seriesIndices.includes(y)) {
                                return (<th key={x} className="ignored">{cellContent}</th>);
                            } else {
                                // Highlight the series name
                                return (<th key={x} className="highlight-series" >{cellContent}</th>);
                            }
                            // Render a numeric cell
                        } else {
                            if (!this.seriesIndices.includes(y)) {
                                // Ignore all invalid series
                                return (<td key={x} className="ignored">{cellContent}</td>);
                            } else {
                                // Only accept the first valid series when we're importing a pie series
                                return (<td key={x} className={(!!pieSeries && this.seriesIndices[0] !== y) ? "ignored" : ""}>{cellContent}</td>);
                            }
                        }
                    });
                    let seriesLabel = null;
                    // Ignore series labels if this data is for pie chart
                    if (!pieSeries) {
                        seriesLabel = (
                            <th
                                key={`series-${y}`}
                                className="table-label"
                                width="1%"
                            >{y >= seriesStart ? `Series ${y + (1 - seriesStart)}` : null}</th>
                        );
                    }
                    return (
                        <tr key={`data-row-${y}`}>
                            {seriesLabel}
                            {rowContent}
                        </tr>
                    );
                }),
            ];

            const toolipContentStyle = {
                width: 250,
                height: 180,
                backgroundImage: `url("${getStaticUrl("/images/dialogs/axis-tooltip.png")}")`,
            };
            const axisInfo = (
                <Tooltip
                    PopperProps={{
                        popperRef: this.tooltipRef,
                    }}
                    title={(<div style={toolipContentStyle}></div>)}
                    classes={{ popper: "popper-white" }}
                    placement="top"
                    arrow
                    interactive
                >
                    <Info color="primary" />
                </Tooltip>
            );

            dialogContent = (
                <>
                    <DialogTitleSplitter>
                        <DialogTitle>
                            <span style={{ marginRight: 6 }}>Import Data</span>
                            {axisInfo}
                        </DialogTitle>

                        {
                            canAppendSeries &&
                            <ToggleButtonGroup
                                value={appendData ? "add" : "replace"}
                                exclusive
                                onChange={(event, value) => {
                                    if (value) {
                                        this.setState({ appendData: value === "add" });
                                    }
                                }}
                                aria-label="Add as New series"
                            >
                                <ToggleButton
                                    value={"replace"}
                                    aria-label="Replace Chart Data"
                                >Replace Chart Data</ToggleButton>
                                <ToggleButton
                                    value={"add"}
                                    aria-label="Add as New Series"
                                >Add as New Series</ToggleButton>
                            </ToggleButtonGroup>
                        }
                    </DialogTitleSplitter>
                    <ChartDialogContent className="import_chart_data_dialog">
                        <div>
                            <Tooltip
                                title="Transpose rows and columns"
                                placement="top"
                                arrow
                            >
                                <Button
                                    aria-label="Transpose rows and columns"
                                    variant="contained"
                                    onClick={() => {
                                        this.processInput({ plotColumnsAsSeries: !plotColumnsAsSeries });
                                    }}
                                >
                                    <img src={getStaticUrl("/images/dialogs/transpose.svg")} />
                                </Button>
                            </Tooltip>
                        </div>
                        <div className="table-container">
                            <div className="table-wrapper">
                                <table className="table">
                                    <tbody>{tableContent}</tbody>
                                </table>
                            </div>
                            <div className="content-toolip-parent">
                                {
                                    !pieSeries &&
                                    <CheckBox
                                        checked={hasSeriesNames}
                                        value="hasSeriesNames"
                                        label={(
                                            <CompositeLabel className="highlight-series">
                                                <span>Series Labels</span>
                                            </CompositeLabel>
                                        )}
                                        className="control"
                                        color="primary"
                                        onChange={hasSeriesNames => {
                                            this.processInput({ hasSeriesNames });
                                        }}
                                    />
                                }
                                <CheckBox
                                    checked={hasCategoryNames}
                                    value="hasCategoryNames"
                                    label={(
                                        <CompositeLabel className="highlight-category">
                                            <span>Category (X-Axis) Labels</span>
                                        </CompositeLabel>
                                    )}
                                    className="control"
                                    color="primary"
                                    onChange={hasCategoryNames => {
                                        this.processInput({ hasCategoryNames });
                                    }}
                                />
                            </div>
                        </div>
                        {
                            error &&
                            <ErrorPane>{error}</ErrorPane>
                        }
                    </ChartDialogContent>
                    <ChartDialogActions>
                        <Button
                            color="secondary"
                            onClick={() => this.props.closeDialog()}
                        >Cancel</Button>
                        <Button
                            color="primary"
                            onClick={() => this.updateChartData()}
                            disabled={!!error}
                        >Import</Button>
                    </ChartDialogActions>
                </>
            );
        }

        return (
            <BeautifulDialog
                maxWidth="md"
                closeDialog={this.props.closeDialog}
            >
                {dialogContent}
            </BeautifulDialog>
        );
    }
}
